import * as angular from 'angular';
import * as Chart from 'chart.js';
import * as moment from 'moment';
import * as _ from 'lodash';
import {LineEquipmentGroup, LineEquipmentItem} from "../../models/counts/lineCounts";
import {apsEnums} from "../../shared/dto/aps.enums";

class EquipmentGroupMoldDistributionGraphController {
  private chart: Chart;
  private dataSets: Chart.ChartDataSets[];
  group: LineEquipmentGroup;
  width: number = 400;
  height: number = 200;
  pointInterval: apsEnums.CountInterval = apsEnums.CountInterval.TenMinutes;
  private $document: angular.IDocumentService;
  private pointIntervalMs: number;
  private equipment: LineEquipmentItem[] = [];
  private equipmentUpdated: { [equipmentId: string]: number } = {};
  private moldList: string[] = [];

  constructor(
    $scope: angular.IScope,
    $timeout: angular.ITimeoutService,
    $document: angular.IDocumentService) {
    this.$document = $document;

    $timeout(() => {
      for (let key in this.group.equipment) {
        this.equipment.push(this.group.equipment[key]);
      }
      this.dataSets = this.createChartDataSets(this.equipment);
      this.pointIntervalMs = this.getPointIntervalMs(this.pointInterval);
      let ctx: any = $document.find('#chart')[0];
      this.chart = new Chart(ctx.getContext('2d'), {
        type: 'bar',
        data: {
          datasets: this.dataSets,
          labels: this.moldList
        },
        options: {
          tooltips: {
            mode: 'index',
            callbacks: {
              label: (item, data) => data.datasets[item.datasetIndex].label + ': ' + data.datasets[item.datasetIndex].data[item.index]
            }
          },
          // pan: {
          //   enabled: false
          // },
          // zoom: {
          //   enabled: false
          // },
          scales: {
            xAxes: [{
              stacked: true
            }],
            yAxes: [{
              type: 'linear',
              stacked: true
            }]
          }
        }
      });
      this.updateData();
      this.group.groupUpdated = this.updateData;
    });

    $scope.$on('$destroy', () => {
      this.group.groupUpdated = null;
    });
  }

  private getPointIntervalMs(interval: apsEnums.CountInterval) {
    let minute = 1000 * 60;

    switch (interval) {
      case (apsEnums.CountInterval.Minute):
        return minute;

      case (apsEnums.CountInterval.TenMinutes):
        return minute * 10;

      case (apsEnums.CountInterval.Hour):
        return minute * 60;

      case (apsEnums.CountInterval.Day):
        return minute * 60 * 24;
    }
  }

  private updateData() {
    let newCountsAdded = false;

    for (let i = 0; i < this.equipment.length; i++) {
      let newestCount = _.first(_.orderBy(this.equipment[i].inspectCount.filteredCounts, ['intervalEndTime'], ['desc']));
      let data = this.dataSets[i].data;
      if (newestCount.intervalEndTime > this.equipmentUpdated[this.equipment[i].id]) {
        let updatedTimeBracket = this.getTimeBracket(this.equipmentUpdated[this.equipment[i].id]);
        let newPoints = this.createIntervalDataPoints(this.equipment[i], updatedTimeBracket);

        let oldestCount = _.first(_.orderBy(this.equipment[i].inspectCount.filteredCounts, 'intervalEndTime'));

        _.remove(data as Chart.ChartPoint[], p => p.x === updatedTimeBracket);
        _.remove(data as Chart.ChartPoint[], p => p.x < oldestCount.intervalEndTime);
        (data as number[]).push(...newPoints);
        newCountsAdded = true;
      }
    }

    if (newCountsAdded) {
      this.chart.update();
    }
  }

  private createChartDataSets(equipments: LineEquipmentItem[]): Chart.ChartDataSets[] {
    let lineColors = [
      'rgba(181, 14, 9, 0.5)', 'rgba(10, 88, 214, 0.5)', 'rgba(13, 150, 59, 0.5)', 'rgba(181, 115, 9, 0.5)'
    ];
    let index = -1;
    equipments = _.sortBy(equipments, (x) => {return -x.line.number; });
    let dataSets = equipments.map(x => {
      index++;
      return {
        label: `${x.name}-${x.line.name}`,
        data: this.createIntervalDataPoints(x),
        backgroundColor: lineColors[index % 4]
      };
    });
    let moldList: string[] = [];
    dataSets.forEach(ds => {
      for (let i = 0; i < 128; i++) {
        if (ds.data[i]) {
          moldList.push(i.toString(10));
        }
      }
    });
    moldList = moldList.filter((v, i, s) =>
      s.indexOf(v) === i).sort((x1, x2) => Number(x1) - Number(x2));
    moldList.forEach(mold => {
      let anyCount: boolean = false;
      dataSets.forEach(ds => {
        if (ds.data[mold] && ds.data[mold] > 0) {
          anyCount = true;
        }
      });
      if (anyCount) {
        dataSets.forEach(ds => {
            if (!ds.data[mold]) {
              ds.data[mold] = 0;
            }
          }
        );
      }
    });
    dataSets.forEach(ds => {
      let newData: number[] = [];
      moldList.forEach( m => {
        newData.push(ds.data[m]);
      });
      ds.data = newData;
      }
    );
    this.moldList = moldList;
    return dataSets;
  }

  private getTimeBracket(time: number, intervalMinutes: number = 10) {
    let end = moment(time);
    return end.add('minutes', intervalMinutes - end.minute() % intervalMinutes).valueOf();
  }

  private createIntervalDataPoints(equipment: LineEquipmentItem, lastUpdatedBracket: number = null) {
    let inspectCounts = _.sortBy(equipment.inspectCount.filteredCounts, x => x.intervalEndTime);
    let startIndex = 0;
    if (lastUpdatedBracket) {
      startIndex = _.findIndex(inspectCounts, x => x.intervalEndTime > lastUpdatedBracket - this.pointIntervalMs
        && x.intervalEndTime <= lastUpdatedBracket);
    }
    let count: number[] = [];
    for (let i = startIndex; i < inspectCounts.length; i++) {
      for (let mc in inspectCounts[i].moldCounts) {
        if (!count[mc]) {
           count[mc] = 0;
        }
        count[mc] += inspectCounts[i].moldCounts[mc].value;
      }
    }
    return count;
  }
}

export default angular.module('aps.controller.equipmentGroupMoldDistributionGraph', [])
  .controller('EquipmentGroupMoldDistributionGraphCtrl', EquipmentGroupMoldDistributionGraphController)
  .name


// WEBPACK FOOTER //
// ./src/app/controllers/counts/equipmentGroupMoldDistributionGraph.controller.ts